import { NgIf } from '@angular/common';
import {
    AfterViewChecked,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormsModule,
    NgForm,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { AuthService } from 'app/core/auth/auth.service';
import { map, mergeMap } from 'rxjs';
import { PasswordStrengthComponent } from '../password-strength/password-strength.component';

@Component({
    selector: 'sign-up-form',
    templateUrl: './sign-up-form.component.html',
    styleUrls: ['./sign-up-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
    imports: [
        NgIf,
        FuseAlertComponent,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        PasswordStrengthComponent
    ],
})
export class SignUpFormComponent implements OnInit, AfterViewChecked {
    @ViewChild('signUpNgForm') signUpNgForm: NgForm;
    @Output() successEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() errorEvent: EventEmitter<any> = new EventEmitter<any>();

    alert: { type: FuseAlertType; message: string } = {
        type: 'success',
        message: '',
    };
    signUpForm: UntypedFormGroup;
    showAlert: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _authService: AuthService,
        private _formBuilder: UntypedFormBuilder,
        private _changeDetectorRef: ChangeDetectorRef
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Create the form
        this.signUpForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
        });
    }

    ngAfterViewChecked() {
        this._changeDetectorRef.detectChanges();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Sign up
     */
    signUp(): void {
        // Do nothing if the form is invalid
        if (this.signUpForm.invalid) {
            return;
        }

        // Disable the form
        this.signUpForm.disable();

        // Hide the alert
        this.showAlert = false;

        this._authService
            .signUp(this.signUpForm.value)
            .pipe(
                map((res) => {
                    return res;
                }),
                mergeMap(() =>
                    this._authService.signIn({
                        username: this.signUpForm.value.email,
                        password: this.signUpForm.value.password,
                        keepMeSignedIn: false,
                    })
                )
            )
            .subscribe({
                next: () => {
                    // Set the alert
                    this.alert = {
                        type: 'success',
                        message: 'You account has been created successfully.',
                    };
                    this.showAlert = true;
                    this.successEvent.emit(this.signUpForm.value);
                },
                error: (response) => {
                    // Re-enable the form
                    this.signUpForm.enable();

                    // Reset the form
                    this.signUpNgForm.resetForm();
                    let error = 'Something went wrong, please try again.';

                    if (typeof response?.message === 'string') {
                        error = response?.message || error;
                    }
                    // Set the alert
                    this.alert = {
                        type: 'error',
                        message: error,
                    };

                    // Show the alert
                    this.showAlert = true;

                    this.errorEvent.emit(response);
                },
            });
    }

    checkValidPassword(isValid: boolean) {
        const control = this.signUpForm.get('password');
        if (!isValid && !control.hasError('invalid')) {
            control.setErrors({ invalid: true });
            return;
        }
        if (isValid && control.hasError('invalid')) {
            control.setErrors(null);
        }
    }
}
